import { RangeListEnum } from './types';

// Calendar-period windows offered IN ADDITION to the trailing RangeListEnum
// ranges. Deliberately NOT part of RangeListEnum: that base enum is shared by
// the histogram and last-orders controls, which must stay 1D..ALL.
export const CALENDAR_RANGES = ['MTD', 'YTD'] as const;
export type CalendarRange = (typeof CALENDAR_RANGES)[number];

// The extended option set used by the Index Comparison tab AND the account
// equity chart: trailing ranges first, then calendar ranges.
export type ExtendedRange = RangeListEnum | CalendarRange;
export const EXTENDED_RANGES: ExtendedRange[] = [
  ...Object.values(RangeListEnum),
  ...CALENDAR_RANGES,
];

export const isCalendarRange = (range: ExtendedRange): range is CalendarRange =>
  (CALENDAR_RANGES as readonly string[]).includes(range);

// Single source of truth for which days fall in an MTD/YTD window, shared by
// the indexed-comparison indexer and the account-equity chart so the two agree
// exactly. Mirrors performance-card's applyScope: "current" month/year from
// new Date() (local now), compared against each day's calendar date.
//
// `inPeriod` takes a day timestamp in SECONDS (UTC midnight, as every
// portfolio-performance series is keyed). `boundary` is the seconds-timestamp
// of the period's first day — the prior-period close sits strictly before it.
export const calendarWindow = (
  range: CalendarRange,
): { inPeriod: (tsSeconds: number) => boolean; boundary: number } => {
  const now = new Date();
  const cy = now.getFullYear();
  const cm = now.getMonth();
  const inPeriod = (tsSeconds: number): boolean => {
    const d = new Date(tsSeconds * 1000);
    if (d.getUTCFullYear() !== cy) return false;
    return range === 'YTD' ? true : d.getUTCMonth() === cm;
  };
  const boundary = Date.UTC(cy, range === 'YTD' ? 0 : cm, 1) / 1000;
  return { inPeriod, boundary };
};
